import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import type { AdminBed, AdminWard } from '@wodi/shared';
import {
  Button,
  Cell,
  Checkbox,
  Column,
  Dialog,
  DialogActions,
  LoadingBlock,
  Notice,
  PageHeader,
  Row,
  Section,
  Select,
  Table,
  TableBody,
  TableHeader,
  Tag,
  TextArea,
  TextField,
} from '@wodi/ui';
import { useState } from 'react';

import {
  adminWardsQuery,
  bedDayItemsQuery,
  createBed,
  createRoom,
  createWard,
  updateBed,
  updateWard,
} from '../api/inpatient.ts';
import { can, sessionQuery } from '../api/session.ts';
import { storesQuery } from '../api/stock.ts';
import { useOnline } from '../hooks/use-online.ts';
import { SettingsPage } from '../routes/settings-nav.tsx';
import { toasts } from '../toasts.ts';
import { errorText } from './bed-board-page.tsx';
import styles from './inpatient.module.css';

// Settings > Wards and beds (docs/plans/phase2-inpatient-adt.md §6): the facility's wards,
// the bed-day service each charges, their rooms and beds. Nothing is deleted: wards are
// switched off and beds retired with a reason.

const BED_STATUS_TAGS: Record<
  AdminBed['status'],
  [string, 'done' | 'attention' | 'neutral' | 'pending']
> = {
  available: ['In use', 'done'],
  cleaning: ['Cleaning', 'pending'],
  blocked: ['Blocked', 'attention'],
  retired: ['Retired', 'neutral'],
};

function useRefresh() {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: adminWardsQuery.queryKey });
    await queryClient.invalidateQueries({ queryKey: ['inpatient'] });
  };
}

function AddWard() {
  const online = useOnline();
  const refresh = useRefresh();
  const items = useQuery(bedDayItemsQuery);
  const [name, setName] = useState('');
  const [itemId, setItemId] = useState<string | null>(null);
  const add = useMutation({
    mutationFn: () => createWard({ name: name.trim(), bedDayServiceItemId: itemId ?? '' }),
    onSuccess: async (ward) => {
      await refresh();
      toasts.show({ title: 'Ward added', description: ward.name });
      setName('');
      setItemId(null);
    },
  });
  return (
    <Section title="Add a ward">
      <form
        className={styles.form}
        aria-label="Add a ward"
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim() !== '' && itemId !== null) add.mutate();
        }}
      >
        <div className={styles.row}>
          <TextField label="Ward name" value={name} onChange={setName} />
          {items.isError ? (
            <Notice tone="error">
              {errorText(items.error, "Bed-day services couldn't be loaded. Try again.")}
            </Notice>
          ) : (
            <Select
              label="Bed day charged as"
              placeholder={items.isPending ? 'Loading…' : 'Choose a bed-day service'}
              description={
                items.isSuccess && items.data.items.length === 0
                  ? 'No bed-day services yet. Add one in Settings, Services and prices.'
                  : 'A service in the bed-day category, priced in the price lists.'
              }
              options={(items.data?.items ?? []).map((i) => ({
                id: i.id,
                label: `${i.name} (${i.code})`,
              }))}
              value={itemId}
              onChange={(v) => {
                setItemId(String(v));
              }}
            />
          )}
        </div>
        {add.error === null ? null : (
          <Notice tone="error">{errorText(add.error, "The ward wasn't added. Try again.")}</Notice>
        )}
        <div className={styles.actions}>
          <Button
            type="submit"
            isDisabled={!online || name.trim() === '' || itemId === null}
            isPending={add.isPending}
            pendingLabel="Adding…"
          >
            Add ward
          </Button>
        </div>
      </form>
    </Section>
  );
}

function RetireDialog({ bed, onClose }: { bed: AdminBed; onClose: () => void }) {
  const online = useOnline();
  const refresh = useRefresh();
  const [reason, setReason] = useState('');
  const retire = useMutation({
    mutationFn: () => updateBed(bed.id, { retired: true, reason: reason.trim() }),
    onSuccess: async () => {
      await refresh();
      toasts.show({ title: 'Bed retired', description: `Bed ${bed.label}` });
      onClose();
    },
  });
  return (
    <Dialog
      title={`Retire bed ${bed.label}`}
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <TextArea label="Why is the bed retired?" value={reason} onChange={setReason} autoFocus />
      {retire.error === null ? null : (
        <Notice tone="error">
          {errorText(retire.error, "The bed wasn't retired. Try again.")}
        </Notice>
      )}
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          isDisabled={!online || reason.trim().length < 5}
          isPending={retire.isPending}
          pendingLabel="Retiring…"
          onPress={() => {
            retire.mutate();
          }}
        >
          Retire bed
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * The store the ward keeps its own stock in, for ward-stock medicine doses (eMAR plan §1.1
 * item 4). Needs `stock.read` to list the stores.
 */
function WardStockStore({
  ward,
  isDisabled,
  onChange,
}: {
  ward: AdminWard;
  isDisabled: boolean;
  onChange: (storeId: string | null) => void;
}) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const mayList = session !== null && can(session, 'stock.read');
  const stores = useQuery({ ...storesQuery, enabled: mayList });
  if (!mayList)
    return (
      <p className={styles.meta}>
        {ward.stockStoreId === null
          ? 'This ward keeps no stock of its own.'
          : 'This ward keeps its own stock. Someone with stock access can change the store.'}
      </p>
    );
  const options = [
    { id: 'none', label: 'No ward stock' },
    ...(stores.data ?? [])
      .filter((st) => st.status === 'active' || st.id === ward.stockStoreId)
      .map((st) => ({ id: st.id, label: st.name })),
  ];
  return (
    <Select
      label="Ward stock store"
      description="Where this ward keeps medicines given dose by dose. Fill it by a transfer from the pharmacy."
      options={options}
      value={ward.stockStoreId ?? 'none'}
      isDisabled={isDisabled || !stores.isSuccess}
      onChange={(v) => {
        const id = String(v);
        onChange(id === 'none' ? null : id);
      }}
    />
  );
}

function WardSection({ ward }: { ward: AdminWard }) {
  const online = useOnline();
  const refresh = useRefresh();
  const [label, setLabel] = useState('');
  const [roomId, setRoomId] = useState<string>('none');
  const [roomName, setRoomName] = useState('');
  const [retiring, setRetiring] = useState<AdminBed | null>(null);
  const addBed = useMutation({
    mutationFn: () =>
      createBed(ward.id, { label: label.trim(), roomId: roomId === 'none' ? null : roomId }),
    onSuccess: async () => {
      await refresh();
      toasts.show({ title: 'Bed added', description: `${ward.name}, bed ${label.trim()}` });
      setLabel('');
    },
  });
  const addRoom = useMutation({
    mutationFn: () => createRoom(ward.id, { name: roomName.trim() }),
    onSuccess: async () => {
      await refresh();
      toasts.show({ title: 'Room added', description: roomName.trim() });
      setRoomName('');
    },
  });
  const toggle = useMutation({
    mutationFn: () =>
      updateWard(ward.id, { status: ward.status === 'active' ? 'inactive' : 'active' }),
    onSuccess: refresh,
  });
  const bringBack = useMutation({
    mutationFn: (bedId: string) => updateBed(bedId, { retired: false }),
    onSuccess: refresh,
  });
  const setStore = useMutation({
    mutationFn: (storeId: string | null) => updateWard(ward.id, { stockStoreId: storeId }),
    onSuccess: async () => {
      await refresh();
      toasts.show({ title: 'Ward stock store saved' });
    },
  });
  const setLabour = useMutation({
    mutationFn: (isLabourWard: boolean) => updateWard(ward.id, { isLabourWard }),
    onSuccess: async () => {
      await refresh();
      toasts.show({ title: 'Ward saved' });
    },
  });
  const roomOf = (id: string | null) => ward.rooms.find((r) => r.id === id)?.name ?? '';
  const error =
    addBed.error ??
    addRoom.error ??
    toggle.error ??
    bringBack.error ??
    setStore.error ??
    setLabour.error;
  return (
    <Section
      title={ward.name}
      description={`Bed day charged as ${ward.bedDayItem.name} (${ward.bedDayItem.code}).`}
      actions={
        <>
          <Tag state={ward.status === 'active' ? 'active' : 'neutral'}>
            {ward.status === 'active' ? 'Active' : 'Switched off'}
          </Tag>
          <Button
            dense
            variant="quiet"
            isDisabled={!online}
            onPress={() => {
              toggle.mutate();
            }}
          >
            {ward.status === 'active' ? 'Switch off' : 'Switch on'}
          </Button>
        </>
      }
    >
      <Table flat dense aria-label={`Beds in ${ward.name}`}>
        <TableHeader>
          <Column isRowHeader>Bed</Column>
          <Column>Room</Column>
          <Column>Status</Column>
          <Column>Actions</Column>
        </TableHeader>
        <TableBody items={ward.beds} renderEmptyState={() => 'No beds yet. Add the first below.'}>
          {(b) => (
            <Row id={b.id}>
              <Cell>{b.label}</Cell>
              <Cell>{roomOf(b.roomId)}</Cell>
              <Cell>
                <Tag state={b.occupied ? 'active' : BED_STATUS_TAGS[b.status][1]}>
                  {b.occupied ? 'Occupied' : BED_STATUS_TAGS[b.status][0]}
                </Tag>
              </Cell>
              <Cell>
                {b.status === 'retired' ? (
                  <Button
                    dense
                    variant="quiet"
                    isDisabled={!online}
                    onPress={() => {
                      bringBack.mutate(b.id);
                    }}
                  >
                    Bring back
                  </Button>
                ) : (
                  <Button
                    dense
                    variant="quiet"
                    isDisabled={!online || b.occupied}
                    onPress={() => {
                      setRetiring(b);
                    }}
                  >
                    Retire
                  </Button>
                )}
              </Cell>
            </Row>
          )}
        </TableBody>
      </Table>
      <WardStockStore
        ward={ward}
        isDisabled={!online || setStore.isPending}
        onChange={(storeId) => {
          setStore.mutate(storeId);
        }}
      />
      {/* A labour ward (docs/plans/phase2-maternity-mch.md §2.1, M5). */}
      <Checkbox
        isSelected={ward.isLabourWard}
        isDisabled={!online || setLabour.isPending}
        description="Listed for maternity. A labour record can still be opened on any stay."
        onChange={(on) => {
          setLabour.mutate(on);
        }}
      >
        Labour ward
      </Checkbox>
      <form
        className={styles.form}
        aria-label={`Add a bed to ${ward.name}`}
        onSubmit={(e) => {
          e.preventDefault();
          if (label.trim() !== '') addBed.mutate();
        }}
      >
        <div className={styles.row}>
          <TextField
            label="New bed label"
            description="As painted on the bed, e.g. B3"
            value={label}
            onChange={setLabel}
          />
          <Select
            label="Room"
            options={[
              { id: 'none', label: 'No room (open ward)' },
              ...ward.rooms
                .filter((r) => r.status === 'active')
                .map((r) => ({ id: r.id, label: r.name })),
            ]}
            value={roomId}
            onChange={(v) => {
              setRoomId(String(v));
            }}
          />
        </div>
        <div className={styles.actions}>
          <Button
            type="submit"
            isDisabled={!online || label.trim() === ''}
            isPending={addBed.isPending}
            pendingLabel="Adding…"
          >
            Add bed
          </Button>
        </div>
      </form>
      <form
        className={styles.row}
        aria-label={`Add a room to ${ward.name}`}
        onSubmit={(e) => {
          e.preventDefault();
          if (roomName.trim() !== '') addRoom.mutate();
        }}
      >
        <TextField label="New room" value={roomName} onChange={setRoomName} />
        <div className={styles.actions}>
          <Button
            type="submit"
            variant="secondary"
            isDisabled={!online || roomName.trim() === ''}
            isPending={addRoom.isPending}
            pendingLabel="Adding…"
          >
            Add room
          </Button>
        </div>
      </form>
      {error === null ? null : (
        <Notice tone="error">{errorText(error, "That change wasn't saved. Try again.")}</Notice>
      )}
      {retiring === null ? null : (
        <RetireDialog
          bed={retiring}
          onClose={() => {
            setRetiring(null);
          }}
        />
      )}
    </Section>
  );
}

export function WardsSettingsPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const wards = useQuery(adminWardsQuery);
  if (session === null || !can(session, 'inpatient.wards.manage'))
    return (
      <>
        <PageHeader title="Wards and beds" />
        <Notice tone="info">
          You don't have permission to set up wards. Ask an administrator if you need it.
        </Notice>
      </>
    );
  return (
    <SettingsPage title="Wards and beds">
      {wards.isPending ? <LoadingBlock label="Loading wards" rows={4} /> : null}
      {wards.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense onPress={() => void wards.refetch()}>
              Try again
            </Button>
          }
        >
          {errorText(wards.error, "Wards couldn't be loaded. Try again.")}
        </Notice>
      ) : null}
      {wards.isSuccess && wards.data.length === 0 ? (
        <Notice tone="info">No wards yet. Add the first one below.</Notice>
      ) : null}
      {(wards.data ?? []).map((w) => (
        <WardSection key={w.id} ward={w} />
      ))}
      <AddWard />
    </SettingsPage>
  );
}
